import React from 'react';

interface ImportExportVisualProps {
  className?: string;
}

/**
 * Import/Export mesh gradient visual
 * Uses CSS custom properties for dynamic theming
 * Apply className to control colors via SCSS
 * Updated to wider format (2648x576) for better horizontal layout
 * Supports ref forwarding for GSAP animations
 */
export const ImportExportVisual = React.forwardRef<SVGSVGElement, ImportExportVisualProps>(
  ({ className }, ref) => {
    return (
      <svg 
        ref={ref}
        viewBox="0 0 2648 576" 
        className={className}
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid slice"
        style={{ width: '100%', height: '100%' }}
      >
      {/* Background */}
      <rect 
        width="2648" 
        height="576" 
        className="import-export-visual__bg"
      />

      {/* Mesh gradients - blurred color blobs */}
      <g opacity="0.5">
        {/* Right mesh (blue to orange) */}
        <g filter="url(#filter-right)">
          <path 
            d="M1393.31 290.873C1393.31 269.465 1393.31 258.761 1398.37 250.754C1400.34 247.642 1402.82 244.885 1405.7 242.598C1413.13 236.713 1423.77 235.574 1445.06 233.296L1738.93 201.842C1764.77 199.077 1777.69 197.694 1787.08 203.315C1790.7 205.482 1793.87 208.329 1796.41 211.699C1803 220.442 1803 233.434 1803 259.419V319.105C1803 344.751 1803 357.574 1796.52 366.272C1794.02 369.627 1790.9 372.472 1787.33 374.652C1778.08 380.303 1765.31 379.124 1739.77 376.765L1445.89 349.623C1424.29 347.628 1413.49 346.63 1405.93 340.751C1403 338.467 1400.48 335.7 1398.47 332.569C1393.31 324.506 1393.31 313.658 1393.31 291.963V290.873Z" 
            className="import-export-visual__mesh-right"
          />
        </g>

        {/* Center ellipse (red to orange gradient) */}
        <g filter="url(#filter-ellipse)">
          <circle 
            cx="1374.08" 
            cy="288" 
            r="159" 
            className="import-export-visual__mesh-center"
          />
        </g>

        {/* Left mesh (blue to red) */}
        <g filter="url(#filter-left)">
          <path 
            d="M862.595 223.134C868.405 212.497 871.311 207.178 877.411 202.82C879.55 201.292 882.705 199.627 885.173 198.722C892.212 196.143 899.423 196.862 913.845 198.299L1201.93 227.007C1223.38 229.144 1234.1 230.212 1241.59 236.095C1244.5 238.38 1247 241.142 1248.99 244.264C1254.1 252.299 1254.1 263.075 1254.1 284.627V292.174C1254.1 313.727 1254.1 324.503 1248.99 332.538C1247 335.66 1244.5 338.422 1241.59 340.707C1234.1 346.59 1223.38 347.658 1201.93 349.795L918.192 378.064C902.093 379.667 894.043 380.469 886.639 377.525C883.94 376.452 880.856 374.681 878.569 372.891C872.293 367.981 869.278 361.39 863.248 348.209C840.389 298.241 840.333 263.885 862.595 223.134Z" 
            className="import-export-visual__mesh-left"
          />
        </g>
      </g>

      {/* Connection lines - broken into individual paths for animation */}
      <g opacity="0.6">
        {/* Line 1 - Top curved line (left side) */}
        <path 
          d="M0 446.5H752.735C761.978 446.5 771.191 445.428 780.187 443.305L1176.51 349.763C1185.58 347.622 1194.87 346.541 1204.19 346.541H1294" 
          className="import-export-visual__line import-export-visual__line-1"
          strokeLinecap="round"
        />
        <path 
          d="M0 446.5H752.735C761.978 446.5 771.191 445.428 780.187 443.305L1176.51 349.763C1185.58 347.622 1194.87 346.541 1204.19 346.541H1294" 
          className="import-export-visual__line-glow import-export-visual__line-1-glow"
          strokeLinecap="round"
        />
        
        {/* Line 1 - Top curved line (right side) */}
        <path 
          d="M1354 346.541H1443.81C1453.13 346.541 1462.42 347.622 1471.49 349.763L1867.81 443.305C1876.81 445.428 1886.02 446.5 1895.26 446.5H2648" 
          className="import-export-visual__line import-export-visual__line-2"
          strokeLinecap="round"
        />
        <path 
          d="M1354 346.541H1443.81C1453.13 346.541 1462.42 347.622 1471.49 349.763L1867.81 443.305C1876.81 445.428 1886.02 446.5 1895.26 446.5H2648" 
          className="import-export-visual__line-glow import-export-visual__line-2-glow"
          strokeLinecap="round"
        />

        {/* Line 2 - Second line (left side) */}
        <path 
          d="M0 366.533H766.691L1190.18 333.505L1190.2 333.503H1294" 
          className="import-export-visual__line import-export-visual__line-3"
          strokeLinecap="round"
        />
        <path 
          d="M0 366.533H766.691L1190.18 333.505L1190.2 333.503H1294" 
          className="import-export-visual__line-glow import-export-visual__line-3-glow"
          strokeLinecap="round"
        />
        
        {/* Line 2 - Second line (right side) */}
        <path 
          d="M1354 333.503H1457.8L1457.82 333.505L1881.31 366.533H2648" 
          className="import-export-visual__line import-export-visual__line-4"
          strokeLinecap="round"
        />
        <path 
          d="M1354 333.503H1457.8L1457.82 333.505L1881.31 366.533H2648" 
          className="import-export-visual__line-glow import-export-visual__line-4-glow"
          strokeLinecap="round"
        />

        {/* Line 3 - Third line (left side) */}
        <path 
          d="M0 286.565H766.725L766.745 286.567L1190.25 319.965H1294" 
          className="import-export-visual__line import-export-visual__line-5"
          strokeLinecap="round"
        />
        <path 
          d="M0 286.565H766.725L766.745 286.567L1190.25 319.965H1294" 
          className="import-export-visual__line-glow import-export-visual__line-5-glow"
          strokeLinecap="round"
        />
        
        {/* Line 3 - Third line (right side) */}
        <path 
          d="M1354 319.965H1457.75L1881.25 286.567L1881.28 286.565H2648" 
          className="import-export-visual__line import-export-visual__line-6"
          strokeLinecap="round"
        />
        <path 
          d="M1354 319.965H1457.75L1881.25 286.567L1881.28 286.565H2648" 
          className="import-export-visual__line-glow import-export-visual__line-6-glow"
          strokeLinecap="round"
        />

        {/* Line 4 - Bottom curved line (left side) */}
        <path 
          d="M0 206.599H752.617C762.016 206.599 771.382 207.698 780.525 209.875L1176.63 304.178C1185.7 306.336 1194.99 307.427 1204.31 307.427H1294" 
          className="import-export-visual__line import-export-visual__line-7"
          strokeLinecap="round"
        />
        <path 
          d="M0 206.599H752.617C762.016 206.599 771.382 207.698 780.525 209.875L1176.63 304.178C1185.7 306.336 1194.99 307.427 1204.31 307.427H1294" 
          className="import-export-visual__line-glow import-export-visual__line-7-glow"
          strokeLinecap="round"
        />
        
        {/* Line 4 - Bottom curved line (right side) */}
        <path 
          d="M1354 307.427H1443.69C1453.01 307.427 1462.3 306.336 1471.37 304.178L1867.47 209.875C1876.62 207.698 1885.98 206.599 1895.38 206.599H2648" 
          className="import-export-visual__line import-export-visual__line-8"
          strokeLinecap="round"
        />
        <path 
          d="M1354 307.427H1443.69C1453.01 307.427 1462.3 306.336 1471.37 304.178L1867.47 209.875C1876.62 207.698 1885.98 206.599 1895.38 206.599H2648" 
          className="import-export-visual__line-glow import-export-visual__line-8-glow"
          strokeLinecap="round"
        />
      </g>

      {/* SVG Filters and Gradients */}
      <defs>
        {/* Color Gradients - using CSS custom properties for theme awareness */}
        <linearGradient id="gradient-right" x1="1428.03" y1="288.795" x2="1803" y2="288.795" gradientUnits="userSpaceOnUse">
          <stop offset="0" style={{ stopColor: 'var(--gradient-right-start)' }} stopOpacity="0.57" />
          <stop offset="1" style={{ stopColor: 'var(--gradient-right-end)' }} stopOpacity="0.1" />
        </linearGradient>

        <linearGradient id="gradient-center" x1="1533.08" y1="288" x2="1215.08" y2="288" gradientUnits="userSpaceOnUse">
          <stop offset="0" style={{ stopColor: 'var(--gradient-center-start)' }} stopOpacity="0.02" />
          <stop offset="0.9375" style={{ stopColor: 'var(--gradient-center-end)' }} stopOpacity="0.7" />
        </linearGradient>

        <linearGradient id="gradient-left" x1="880.587" y1="288.397" x2="1254.1" y2="288.397" gradientUnits="userSpaceOnUse">
          <stop offset="0" style={{ stopColor: 'var(--gradient-left-start)' }} stopOpacity="0.2" />
          <stop offset="1" style={{ stopColor: 'var(--gradient-left-end)' }} stopOpacity="0.9" />
        </linearGradient>

        {/* Blur Filters */}
        <filter id="filter-right" x="1301.65" y="107.77" width="593.016" height="362.616" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
          <feGaussianBlur stdDeviation="45.8318" result="effect1_foregroundBlur"/>
        </filter>

        <filter id="filter-ellipse" x="1101.32" y="15.2355" width="545.529" height="545.529" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
          <feGaussianBlur stdDeviation="56.8823" result="effect1_foregroundBlur"/>
        </filter>

        <filter id="filter-left" x="754.336" y="105.339" width="591.425" height="365.826" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
          <feGaussianBlur stdDeviation="45.8318" result="effect1_foregroundBlur"/>
        </filter>
      </defs>
    </svg>
  );
});

// Add display name for better debugging
ImportExportVisual.displayName = 'ImportExportVisual';
